// ── FCM push registration (Tier 3) ───────────────────────────────────────────
// Registers this browser for Web Push via Firebase Cloud Messaging and stores
// the token under users/{uid}.fcmTokens so the Vercel cron job
// (api/cron/send-reminders.js) can look it up. Entirely optional/additive:
// no-ops when Firebase isn't configured, the VAPID key is unset, or the
// browser doesn't support push — Tiers 1 & 2 keep working regardless.
import {
  getMessaging, getToken, deleteToken, isSupported,
} from 'firebase/messaging';
import { doc, setDoc, updateDoc, FieldPath, deleteField } from 'firebase/firestore';
import { app, db, isFirebaseConfigured } from './config';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

export const isPushConfigured = Boolean(isFirebaseConfigured && VAPID_KEY);

let swRegPromise = null;
function getSwRegistration() {
  if (!('serviceWorker' in navigator)) return Promise.resolve(null);
  if (swRegPromise) return swRegPromise;
  const cfg = app.options;
  // CRA doesn't template env vars into public/ files, so the config is passed
  // to firebase-messaging-sw.js via query string (a well-known workaround for
  // CRA + FCM). These values are the same public-safe client config already
  // shipped in the JS bundle.
  const params = new URLSearchParams({
    apiKey: cfg.apiKey || '',
    authDomain: cfg.authDomain || '',
    projectId: cfg.projectId || '',
    storageBucket: cfg.storageBucket || '',
    messagingSenderId: cfg.messagingSenderId || '',
    appId: cfg.appId || '',
  });
  swRegPromise = navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params}`, {
    scope: '/firebase-cloud-messaging-push-scope',
  });
  return swRegPromise;
}

// Call after the user enables notifications AND grants permission. Requires a
// signed-in uid (Tier 3 has nowhere offline to store the token).
export async function registerPush(uid) {
  if (!isPushConfigured || !uid) return null;
  try {
    if (!(await isSupported())) return null;
    const reg = await getSwRegistration();
    if (!reg) return null;
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
    if (!token) return null;
    await setDoc(doc(db, 'users', uid), {
      fcmTokens: { [token]: { updatedAt: Date.now(), ua: navigator.userAgent.slice(0, 200) } },
      // Same field the native path writes (src/capacitor/nativePush.js): the
      // reminder crons resolve "HH:MM" against it instead of the server's own
      // UTC clock. See lib/tz.js.
      timeZone: (() => {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
        catch { return 'UTC'; }
      })(),
    }, { merge: true });
    return token;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[Clarity] push registration failed', e);
    return null;
  }
}

// Call when the user disables notifications or signs out.
export async function unregisterPush(uid) {
  if (!isPushConfigured) return;
  try {
    if (!(await isSupported())) return;
    const reg = await getSwRegistration();
    if (!reg) return;
    const messaging = getMessaging(app);
    const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg }).catch(() => null);
    await deleteToken(messaging).catch(() => {});
    if (token && uid) {
      await updateDoc(doc(db, 'users', uid), new FieldPath('fcmTokens', token), deleteField()).catch(() => {});
    }
  } catch { /* best-effort cleanup */ }
}
